// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createColumnHelper, type PaginationState } from '@tanstack/react-table';
import { Table, IconButton } from '@shared/ui-kit/components';
import { capitalizeString } from '@shared/ui-kit/utils';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Tooltip } from '@mui/material';
import { type ClientRow } from '@shared/advisory/aggregation/hooks/use-clients';
import { AdvisorRole } from '@shared/data-access/interfaces/api/engine/citec';

export interface ClientsTableProps {
  readonly data: ClientRow[];
  readonly userId?: number | null;
  readonly role?: AdvisorRole;
  readonly onViewProfile: (clientId: number) => void;
  readonly onSetClientProfile: (clientId: number) => void;
  readonly onDeleteClient: (clientId: number) => void;
  readonly currency: string;
  readonly pagination: PaginationState;
  readonly pageCount: number;
  readonly onPaginationChange: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
}

const statusColorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> =
  {
    active: 'success',
    pending: 'warning',
    inactive: 'error',
    approved: 'success',
    rejected: 'error',
    cancelled: 'error',
  };

const columnHelper = createColumnHelper<ClientRow>();

const DELETE_HIDDEN_ROLES = new Set([
  AdvisorRole.OperationsCitec,
  AdvisorRole.SilverAlpha,
]);

export const ClientsTable: FC<ClientsTableProps> = ({
  data,
  userId,
  role,
  onViewProfile,
  onSetClientProfile,
  onDeleteClient,
  currency,
  pagination,
  pageCount,
  onPaginationChange,
}) => {
  const { t } = useTranslation();

  const canDeleteByRole = role ? !DELETE_HIDDEN_ROLES.has(role) : true;

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        cell: ({ getValue }) => <span className="truncate">{getValue()}</span>,
      }),

      columnHelper.accessor('email', {
        header: t('common.email', { ns: 'common' }),
        cell: ({ getValue }) => {
          const email = getValue();
          if (!email) return null;

          return <a href={`mailto:${email}`}>{email}</a>;
        },
      }),

      columnHelper.accessor('volume', {
        header: t('common.total-volume', {
          ns: 'common',
        }),
        id: 'investment',
        meta: {
          alignment: 'end',
        },
        cell: ({ getValue }) => {
          const value = getValue();

          return <Table.Data value={value} format="currency" currency={currency} />;
        },
      }),

      columnHelper.accessor('status', {
        header: t('common.status', { ns: 'common' }),
        cell: ({ getValue }) => {
          const status = (getValue() as string) ?? '';
          if (!status) return null;

          const chipColor = statusColorMap[status] ?? 'default';
          const statusTranslations: Record<string, string> = {
            active: t('common.active', { ns: 'common' }),
            pending: t('common.pending', { ns: 'common' }),
            inactive: t('common.inactive', { ns: 'common' }),
            approved: t('common.approved', { ns: 'common' }),
            rejected: t('common.rejected', { ns: 'common' }),
            cancelled: t('common.cancelled', { ns: 'common' }),
          };
          const label = statusTranslations[status] ?? capitalizeString(status);

          return (
            <Chip
              label={label}
              variant="filled"
              size="small"
              className="!max-h-6 min-w-20"
              color={chipColor}
            />
          );
        },
      }),

      columnHelper.display({
        id: 'options',
        enableSorting: false,
        meta: { alignment: 'end' },
        cell: ({ row }) => {
          const clientRow = row.original;
          const canDelete =
            canDeleteByRole && clientRow.citec_user_id === userId && !!userId;

          return (
            <div className="flex items-center justify-end gap-x-4">
              <Tooltip
                title={t('accounts.view-profile', { ns: 'components' })}
                placement="top"
              >
                <span>
                  <IconButton
                    icon="Person"
                    variant="icon"
                    size="lg"
                    color="main"
                    onClick={e => {
                      e.stopPropagation();
                      onViewProfile(clientRow.id);
                    }}
                    data-testid={`view-profile-${clientRow.id}`}
                  />
                </span>
              </Tooltip>
              <Tooltip
                title={t('accounts.open-dashboard-tooltip', { ns: 'components' })}
                placement="top"
              >
                <span>
                  <IconButton
                    icon="Dashboard"
                    variant="icon"
                    size="lg"
                    color="main"
                    onClick={e => {
                      e.stopPropagation();
                      onSetClientProfile(clientRow.id);
                    }}
                    data-testid={`open-dashboard-${clientRow.id}`}
                  />
                </span>
              </Tooltip>
              {canDelete && (
                <Tooltip
                  title={t('common.delete', { ns: 'common' })}
                  placement="top"
                >
                  <span>
                    <IconButton
                      icon="Trashcan"
                      variant="icon"
                      size="lg"
                      color="main"
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteClient(clientRow.id);
                      }}
                      data-testid={`delete-client-${clientRow.id}`}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      }),
    ],
    [
      t,
      userId,
      canDeleteByRole,
      onDeleteClient,
      onViewProfile,
      onSetClientProfile,
      currency,
    ],
  );

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        data={data}
        columns={columns}
        className="max-h-[320px]"
        getRowId={row => row.id.toString()}
        enableExpanding={false}
        enablePagination
        manualPagination
        pageCount={pageCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={onPaginationChange}
        initialSorting={[{ id: 'name', desc: false }]}
        data-testid="table-clients"
      />
    </div>
  );
};
